import { Box, Link, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";

interface PersonalUpdateCardProps {
  userName: string;
  welcomeContent?: string | null;
  welcomeMessage?: string | null;
  onExplore?: () => void;
  onKpiSelect?: (kpiDefinitionId: string) => void;
  onAccountabilitySelect?: (accountabilityDefinitionId: string) => void;
  onComponentDefinitionSelect?: (
    componentDefinitionId: string,
    componentDefinitionName: string
  ) => void;
}

type WelcomeLinkHandlers = Omit<
  PersonalUpdateCardProps,
  "userName" | "welcomeContent" | "welcomeMessage"
>;

const SAFE_WELCOME_LINK_PATTERN = /^(?:https?:\/\/|\/(?!\/)|#)/i;
const INLINE_MARKDOWN_PATTERN =
  /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
const BOLD_MARKDOWN_PATTERN = /\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*/g;
const KPI_LINK_PATTERN = /^#kpi-(.+)$/;
const ACCOUNTABILITY_LINK_PATTERN = /^#accountability-(.+)$/;
const COMPONENT_DEFINITION_LINK_PATTERN = /^#component-definition-(.+)$/;
const FOCUS_TERM_PATTERN = /(utilisation|earnings|costs|profitability)/gi;
const SAFE_ROWENA_WELCOME_TAGS = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "del",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];
const SAFE_ROWENA_WELCOME_ATTRIBUTES = [
  "alt",
  "colspan",
  "href",
  "rel",
  "rowspan",
  "scope",
  "title",
];

export function getFirstName(userName: string) {
  return userName.trim().split(/\s+/)[0] || "there";
}

export function formatPersonalUpdateDate(date: Date) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
    .format(date)
    .replace(",", "");
}

function getWelcomeLinkHash(href: string) {
  if (href.startsWith("#")) return href;

  try {
    return new URL(href, "http://localhost").hash;
  } catch {
    return "";
  }
}

function decodeLinkIdentifier(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function handleWelcomeLinkClick(
  event: { preventDefault: () => void },
  href: string,
  linkText: string,
  {
    onExplore,
    onKpiSelect,
    onAccountabilitySelect,
    onComponentDefinitionSelect,
  }: WelcomeLinkHandlers
) {
  const linkHash = getWelcomeLinkHash(href);
  const kpiLinkMatch = linkHash.match(KPI_LINK_PATTERN);
  const accountabilityLinkMatch = linkHash.match(ACCOUNTABILITY_LINK_PATTERN);
  const componentDefinitionLinkMatch = linkHash.match(
    COMPONENT_DEFINITION_LINK_PATTERN
  );

  if (kpiLinkMatch && onKpiSelect) {
    event.preventDefault();
    onKpiSelect(decodeLinkIdentifier(kpiLinkMatch[1]));
  } else if (accountabilityLinkMatch && onAccountabilitySelect) {
    event.preventDefault();
    onAccountabilitySelect(decodeLinkIdentifier(accountabilityLinkMatch[1]));
  } else if (componentDefinitionLinkMatch && onComponentDefinitionSelect) {
    event.preventDefault();
    // The link text is the Component Definition's name.
    onComponentDefinitionSelect(
      decodeLinkIdentifier(componentDefinitionLinkMatch[1]),
      linkText.trim()
    );
  } else if (linkHash === "#my-view" && onExplore) {
    event.preventDefault();
    onExplore();
  }
}

function renderHighlightedText(text: string, keyPrefix: string) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  const pattern = new RegExp(FOCUS_TERM_PATTERN);

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }

    nodes.push(
      <Box
        component="em"
        key={`${keyPrefix}-focus-${match.index}`}
        sx={{
          color: "inherit",
          fontStyle: "italic",
          fontWeight: 800,
        }}
      >
        {match[0]}
      </Box>
    );
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
}

function renderBoldMarkdown(text: string, keyPrefix: string) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  const pattern = new RegExp(BOLD_MARKDOWN_PATTERN);

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) {
      nodes.push(
        ...renderHighlightedText(
          text.slice(cursor, match.index),
          `${keyPrefix}-plain-${cursor}`
        )
      );
    }

    const strongItalicText = match[1];
    const boldText = match[2];
    nodes.push(
      <Box
        component="strong"
        key={`${keyPrefix}-bold-${match.index}`}
        sx={{ color: "inherit", display: "inline", fontWeight: 700 }}
      >
        {strongItalicText ? (
          <Box component="em" sx={{ display: "inline", fontStyle: "italic" }}>
            {strongItalicText}
          </Box>
        ) : (
          boldText
        )}
      </Box>
    );
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    nodes.push(
      ...renderHighlightedText(
        text.slice(cursor),
        `${keyPrefix}-plain-${cursor}`
      )
    );
  }

  return nodes;
}

function renderWelcomeMessageLine(
  line: string,
  lineIndex: number,
  linkHandlers: WelcomeLinkHandlers
) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  const pattern = new RegExp(INLINE_MARKDOWN_PATTERN);

  while ((match = pattern.exec(line))) {
    if (match.index > cursor) {
      nodes.push(
        ...renderHighlightedText(
          line.slice(cursor, match.index),
          `line-${lineIndex}-plain-${cursor}`
        )
      );
    }

    const [, linkLabel, linkHref, boldText, italicText] = match;
    if (linkLabel && linkHref) {
      const href = SAFE_WELCOME_LINK_PATTERN.test(linkHref) ? linkHref : "#";

      nodes.push(
        <Link
          key={`line-${lineIndex}-link-${match.index}`}
          href={href}
          onClick={(event) => {
            handleWelcomeLinkClick(
              event,
              linkHref,
              event.currentTarget.textContent ?? "",
              linkHandlers
            );
          }}
          sx={{
            color: "#3F50B5",
            fontWeight: 700,
            textDecorationColor: "rgba(63, 80, 181, 0.35)",
            textUnderlineOffset: "3px",
            "&:hover": {
              color: "#2F3D94",
              textDecorationColor: "#2F3D94",
            },
            "&:focus-visible": {
              borderRadius: "2px",
              outline: "3px solid rgba(63, 80, 181, 0.2)",
              outlineOffset: 2,
            },
          }}
        >
          {renderBoldMarkdown(
            linkLabel,
            `line-${lineIndex}-link-${match.index}`
          )}
        </Link>
      );
    } else if (boldText) {
      nodes.push(
        <Box
          component="strong"
          key={`line-${lineIndex}-bold-${match.index}`}
          sx={{ color: "inherit", fontWeight: 700 }}
        >
          {boldText}
        </Box>
      );
    } else if (italicText) {
      nodes.push(
        <Box
          component="em"
          key={`line-${lineIndex}-italic-${match.index}`}
          sx={{ color: "inherit", fontStyle: "italic" }}
        >
          {italicText}
        </Box>
      );
    }

    cursor = match.index + match[0].length;
  }

  if (cursor < line.length) {
    nodes.push(
      ...renderHighlightedText(
        line.slice(cursor),
        `line-${lineIndex}-plain-${cursor}`
      )
    );
  }

  return nodes;
}

export function PersonalUpdateHeader({ date = new Date() }: { date?: Date }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.9}
      useFlexGap
      flexWrap="wrap"
      sx={{ px: { xs: 0.5, sm: 0.75 }, pt: { xs: 1, sm: 1.25 } }}
    >
      <Typography
        id="personal-update-title"
        component="h1"
        sx={{
          m: 0,
          color: "#667085",
          fontSize: "0.75rem",
          fontWeight: 800,
          lineHeight: 1.4,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        Personal Update
      </Typography>

      <Box
        component="span"
        aria-hidden="true"
        sx={{ color: "#C0C5D0", fontSize: "0.75rem", lineHeight: 1 }}
      >
        ·
      </Box>

      <Typography
        component="time"
        dateTime={date.toISOString()}
        sx={{
          color: "#7A8497",
          fontSize: "0.75rem",
          fontWeight: 600,
          lineHeight: 1.4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatPersonalUpdateDate(date)}
      </Typography>
    </Stack>
  );
}

export function PersonalUpdateCard({
  userName,
  welcomeContent,
  welcomeMessage,
  ...linkHandlers
}: PersonalUpdateCardProps) {
  const firstName = getFirstName(userName);
  const sanitizedWelcomeContent = useMemo(
    () =>
      welcomeContent?.trim()
        ? DOMPurify.sanitize(welcomeContent, {
            ALLOWED_TAGS: SAFE_ROWENA_WELCOME_TAGS,
            ALLOWED_ATTR: SAFE_ROWENA_WELCOME_ATTRIBUTES,
            ALLOW_DATA_ATTR: false,
          })
        : "",
    [welcomeContent]
  );
  const hasWelcomeContent = Boolean(sanitizedWelcomeContent.trim());
  const messageLines = !hasWelcomeContent
    ? welcomeMessage
        ?.split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean) ?? []
    : [];
  const handleWelcomeContentClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest("a");
    if (!link || !event.currentTarget.contains(link)) return;

    handleWelcomeLinkClick(
      event,
      link.getAttribute("href") ?? "",
      link.textContent ?? "",
      linkHandlers
    );
  };

  return (
    <Box
      component="section"
      aria-label="Personal Update"
      sx={{
        px: { xs: 0.5, sm: 0.75 },
        py: { xs: 1, sm: 1.25 },
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography
        component="p"
        sx={{
          m: 0,
          mt: 1,
          color: "#19345D",
          fontSize: { xs: "1rem", sm: "1.3rem" },
          fontWeight: 800,
          lineHeight: 1.2,
          letterSpacing: "-0.035em",
        }}
      >
        Hi {firstName}
      </Typography>

      {hasWelcomeContent ? (
        <Box
          component="div"
          onClick={handleWelcomeContentClick}
          sx={{
            mt: 1.4,
            color: "#49566C",
            fontSize: "0.9375rem",
            lineHeight: 1.55,
            "& > :first-of-type": { mt: 0 },
            "& > :last-child": { mb: 0 },
            "& p": { my: 0.35 },
            "& a": {
              color: "#3F50B5",
              fontWeight: 700,
              textDecorationColor: "rgba(63, 80, 181, 0.35)",
              textUnderlineOffset: "3px",
            },
            "& a:hover": {
              color: "#2F3D94",
              textDecorationColor: "#2F3D94",
            },
            "& a:focus-visible": {
              borderRadius: "2px",
              outline: "3px solid rgba(63, 80, 181, 0.2)",
              outlineOffset: 2,
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedWelcomeContent }}
        />
      ) : messageLines.length > 0 ? (
        <Typography
          component="p"
          sx={{
            m: 0,
            mt: 1.4,
            color: "#49566C",
            fontSize: "0.9375rem",
            lineHeight: 1.55,
          }}
        >
          {messageLines.map((line, lineIndex) => (
            <Box
              component="span"
              key={`${lineIndex}-${line}`}
              sx={{ display: "block", mt: lineIndex === 0 ? 0 : 0.35 }}
            >
              {renderWelcomeMessageLine(line, lineIndex, linkHandlers)}
            </Box>
          ))}
        </Typography>
      ) : null}
    </Box>
  );
}
